<script setup lang="ts">
import { isLoggedIn } from 'axios-jwt';
import { computed, type ComputedRef, onMounted, watch } from 'vue';
import {
  LoadingSpinner,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { useDisplay } from 'vuetify';
import MainNavigation from '@/components/core/MainNavigation.vue';
import AppBreadcrumbs from '@/components/core/AppBreadcrumbs.vue';
import { patchUserLanguage } from '@/api/UserApi';
import type { User } from '@/models/User';
import gettext from '@/i18n/gettext';
import CoreApi from '@/api/CoreApi';
import useCommonStore from '@/store/CommonStore';
import useNotionStore from '@/store/NotionStore';
import RateExperience from '@/components/core/RateExperience.vue';
import AlertSnackbars from '@/components/atoms/AlertSnackbars.vue';

const { $gettext } = gettext;

CoreApi.initialize();

const commonStore = useCommonStore();
const loadingSpinnerStore = useLoadingSpinnerStore();

const { smAndDown } = useDisplay();

const user: ComputedRef<User> = computed(() => {
  return CoreApi.authStore.user;
});

const environment: ComputedRef<string | undefined> = computed(() => {
  return commonStore.environment;
});

const isAuthenticated: ComputedRef<boolean> = computed(() => {
  return CoreApi.authStore.isAuthenticated;
});

const showBreadcrumbs: ComputedRef<boolean> = computed(() => {
  return !smAndDown.value && isAuthenticated.value;
});

/**
 * Changes the current language in frontend and (if logged in) in backend.
 * @param lang The language to set
 */
async function changeLanguage(lang: string) {
  CoreApi.authStore.setLanguage(lang);
  if (await isLoggedIn()) {
    await patchUserLanguage(user.value);
  }
}

async function logout() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  await CoreApi.authStore.logoutAndClearUserData();
}

onMounted(async () => {
  if (await isLoggedIn()) {
    const boot = (async () => {
      try {
        await CoreApi.authStore.loadUser();
      } finally {
        CoreApi.authStore.markBootCompleted();
      }
    })();
    CoreApi.authStore.setBootPromise(boot);
  } else if (window.location.pathname !== '/login') {
    await logout();
  }
});

watch(
  () => ({
    loggedIn: isAuthenticated.value,
    terms: CoreApi.authStore.termsAccepted,
    permsLoaded: CoreApi.authStore.permissionsLoaded,
  }),
  async ({ loggedIn, terms, permsLoaded }) => {
    if (!loggedIn || !terms) {
      return;
    }
    if (!permsLoaded) {
      await CoreApi.authStore.loadPermissions();
    }
    await CoreApi.authStore.loadProfileOnce();
    useNotionStore().fetchNotionData();
  },
);
</script>

<template>
  <v-app>
    <v-app-bar app>
      <v-btn
        v-if="smAndDown"
        icon="mdi-menu"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />
      <img
        v-if="showBreadcrumbs"
        class="logo-mark cursor-pointer"
        alt="RobCo"
        src="@/assets/robco_logo.svg"
        @click="$router.push('/')"
      />
      <img
        v-else
        class="logo"
        :class="
          $router.currentRoute.value.fullPath !== '/' ? 'cursor-pointer' : ''
        "
        alt="RobCo Studio"
        src="@/assets/robco_studio.png"
        @click="$router.push('/')"
      />
      <app-breadcrumbs v-if="showBreadcrumbs" />
      <v-app-bar-title>
        <v-btn
          v-if="environment !== 'production'"
          class="mx-3"
          style="pointer-events: none; margin-top: -1px"
          color="warning"
          variant="outlined"
          size="x-small"
        >
          {{ environment }} build
        </v-btn>
      </v-app-bar-title>

      <!-- LOGGED IN -->
      <v-menu v-if="isAuthenticated && user" origin="top center" width="256">
        <template v-slot:activator="{ props }">
          <v-btn dark icon v-bind="props" class="menu-button mr-3">
            <v-avatar size="46" class="color-unset">
              <v-img
                v-if="user.profilePic"
                contain
                alt="user-image"
                :src="user.profilePic"
              />
              <v-icon v-else icon="mdi-account" color="black"></v-icon>
            </v-avatar>
          </v-btn>
        </template>

        <v-list nav class="mr-8">
          <v-list-item
            value="profile"
            prepend-icon="mdi-account"
            :title="$gettext('Profile')"
            to="/profile"
          />
          <v-list-item
            value="language"
            prepend-icon="mdi-translate"
            :title="commonStore.humanReadableLanguage"
            @click.stop="
              changeLanguage(commonStore.language === 'en' ? 'de' : 'en')
            "
          />
          <v-list-item
            value="item"
            prepend-icon="mdi-location-exit"
            :title="$gettext('Logout')"
            @click="logout()"
          />
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main>
      <main-navigation></main-navigation>
      <v-container fluid class="fullview">
        <loading-spinner />
        <!-- Keyed by path so param-only navigation (e.g. back/forward between
        two project pages) remounts the view instead of reusing a stale
        instance; query-only changes (tabs) keep the instance alive. -->
        <router-view :key="$route.path"></router-view>
        <rate-experience />

        <AlertSnackbars />
      </v-container>
    </v-main>

    <!--    <footer class="footer pa-2">-->
    <!--      <v-col class="text-center text-white font-weight-bold" cols="12">-->
    <!--        © RobCo GmbH, {{ new Date().getFullYear() }}-->
    <!--      </v-col>-->
    <!--    </footer>-->
  </v-app>
</template>

<style lang="scss">
#app {
  width: 100%;
  height: 100%;
}

.logo-mark {
  margin-left: 20px;
  width: 36px;
  height: 36px;
}

.logo {
  margin-left: 20px;
  @media only screen and (max-resolution: 191dpi) {
    content: url('@/assets/robco_studio.png');
  }

  @media only screen and (min-resolution: 192dpi) {
    width: 200px;
    content: url('@/assets/robco_studio.svg');
  }
}

.v-main {
  overflow-y: auto;
  padding-top: 0 !important;
  margin-top: 64px;
  // margin: 64px 0; // use this in combination with the footer!
  height: calc(
    100% - 64px
  ); // 100% - (64px header menu height + 64px footer height)
}

.menu-button {
  border-radius: 24px !important;
  > .v-btn__overlay {
    opacity: 0 !important;
  }
}

.footer {
  position: absolute;
  background: rgb(var(--v-theme-primary));
  z-index: 999999;
  bottom: 0;
  width: 100%;
}
</style>
